/**
 * AuthorizedByField — the "Authorized by (full name)" input of the Change in Contact (CIC) workflow.
 * CHG-0186 (Bill 2026-11-04). Replaces the legacy "Customer initials" box on every CIC surface.
 *
 * Full name as typed (never upper-cased), at most AUTHORIZED_BY_MAX characters, no minimum.
 *
 * When the dialog opens with a prefill (an officer-created in-branch application), the field gets one
 * gentle amber pulse (once, not looping; with reduced motion a steady amber outline instead) and a hint
 * under it; it stays amber until the officer focuses it or edits it. Confirm is never blocked by the cue
 * and nothing about it goes into the audit note. Colours come from the theme tokens, so the cue follows
 * Teams light / dark / high-contrast.
 */
import { useEffect, useId, useState, type CSSProperties } from 'react';
import { AUTHORIZED_BY_LABEL, AUTHORIZED_BY_MAX, AUTHORIZED_BY_PREFILL_HINT, normalizeAuthorizedBy } from './authorizedBy';
import './AuthorizedByField.css';

export interface AuthorizedByFieldProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** The verification method needs a name (every method except Post Office "0" and Banno "3"). */
  required: boolean;
  /** The value the dialog opened with ('' = not prefilled). Drives the attention cue and the hint. */
  prefill?: string;
  disabled?: boolean;
  /** Host label / input classes, so the field matches the surrounding dialog. */
  labelClassName?: string;
  inputClassName?: string;
  labelStyle?: CSSProperties;
  inputStyle?: CSSProperties;
}

export function AuthorizedByField(props: AuthorizedByFieldProps): JSX.Element {
  const { value, onChange, required, prefill = '', disabled, labelClassName, inputClassName, labelStyle, inputStyle } = props;
  const autoId = useId();
  const id = props.id ?? `authby-${autoId}`;
  const hintId = `${id}-hint`;
  const hasPrefill = prefill.trim().length > 0;

  // Amber until the officer focuses or edits the field; re-armed when a new prefill arrives.
  const [attention, setAttention] = useState<boolean>(hasPrefill);
  useEffect(() => { setAttention(hasPrefill); }, [hasPrefill, prefill]);

  const showHint = hasPrefill && normalizeAuthorizedBy(value) === normalizeAuthorizedBy(prefill);

  return (
    <div className={`authby${attention ? ' authby--attention' : ''}`} data-testid="authorized-by">
      <label className={labelClassName ?? 'authby__label'} style={labelStyle} htmlFor={id}>
        {AUTHORIZED_BY_LABEL}{required ? ' *' : ' (optional)'}
      </label>
      <input
        id={id}
        className={`authby__input${inputClassName ? ` ${inputClassName}` : ''}`}
        style={inputStyle}
        type="text"
        value={value}
        onChange={(e) => { setAttention(false); onChange(e.currentTarget.value); }}
        onFocus={() => setAttention(false)}
        disabled={disabled}
        maxLength={AUTHORIZED_BY_MAX}
        autoComplete="off"
        spellCheck={false}
        placeholder="e.g. Jane Q. Smith"
        aria-required={required}
        {...(showHint ? { 'aria-describedby': hintId } : {})}
      />
      {showHint && (
        <div id={hintId} className="authby__hint">{AUTHORIZED_BY_PREFILL_HINT}</div>
      )}
    </div>
  );
}
